import 'whatwg-fetch';
import { createQueryString } from 'common/utils/queryStringUtils';

export function csrfFromDom() {
  const el = document.querySelector('meta[name="csrf-token"]');
  return el ? el.getAttribute('content') : '';
}

export function isAbortException(exception) {
  return exception instanceof DOMException && exception.name === 'AbortError';
}

export class ResponseNotOkError extends Error {
  constructor(fetchResponse, errorMessage) {
    const message =
      errorMessage ||
      `Network response code not ok. Status: ${fetchResponse.status}`;
    super(message);
    this.message = message;

    // see this.response.status for status code
    // https://developer.mozilla.org/en-US/docs/Web/API/Response
    this.response = fetchResponse;

    // same structure as `code` field here:
    // github.com/axios/axios/blob/364867edd295b5ea0019b4e70fd37be5a5b751ee/index.d.ts#L85-L92
    this.code = fetchResponse.status.toString();
  }
}

// do not make this async -- we are adding .cancel onto the promise returned
function doFetch(method, url, body = null, options = {}) {
  const controller = new window.AbortController();
  const fetchOptions = {
    method,
    credentials: 'include',
    headers: { 'X-CSRF-token': csrfFromDom() },
    signal: controller.signal,
  };
  if (body && !(body instanceof FormData)) {
    fetchOptions.headers['Content-Type'] = 'application/json';
  }
  if (body instanceof FormData || typeof body === 'string') {
    fetchOptions.body = body;
  } else if (body) {
    if (typeof body !== 'object') {
      throw new Error(`Unsupported "body" data type: ${typeof body}`);
    }
    fetchOptions.body = JSON.stringify(body);
  }

  if (options.format === 'json') {
    fetchOptions.headers.Accept = 'application/json';
    delete options.format;
  }

  // eslint-disable-next-line no-async-promise-executor
  const fetchPromise = new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, { ...fetchOptions, ...options });
      if (!response.ok) {
        return await response
          .text()
          .then(text => reject(new ResponseNotOkError(response, text)));
      }
      try {
        const json = await response.json();
        return resolve(json);
      } catch (error) {
        return resolve(response.body);
      }
    } catch (error) {
      return reject(error);
    }
  });
  fetchPromise.cancel = () => controller.abort();
  return fetchPromise;
}

// all methods return a promise with a `.cancel` method
export default {
  get(url, queryParams = null, options = {}) {
    const fullUrl = queryParams
      ? `${url}?${createQueryString(queryParams)}`
      : url;
    return doFetch('GET', fullUrl, null, options);
  },
  put(url, body = undefined, options = {}) {
    return doFetch('PUT', url, body, options);
  },
  patch(url, body = undefined, options = {}) {
    return doFetch('PATCH', url, body, options);
  },
  post(url, body = undefined, options = {}) {
    return doFetch('POST', url, body, options);
  },
  delete(url, options = {}) {
    return doFetch('DELETE', url, null, options);
  },
};
